import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, DoorOpen, ArrowsInSimple, Users, Moon, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { autoSend, channelsLabel } from "@/lib/messaging";

export default function ArrivalsDeparturesDialog({ mode, open, onClose }) {
  // mode: "arrivals" | "departures"
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    const path = mode === "departures" ? "/departures/today" : "/arrivals/today";
    api.get(path).then(r => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [open, mode]);

  const isDep = mode === "departures";
  const title = isDep ? "Abreisen heute" : "Anreisen heute";
  const Icon  = isDep ? ArrowsInSimple : DoorOpen;
  const btnLabel = isDep ? "Danke-Nachricht senden" : "Willkommens-Nachricht senden";

  const trigger = (row) => autoSend(row, isDep ? "thankyou" : "welcome");

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid={`${mode}-today-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={22} weight="duotone" className="text-primary"/>
            {title}
            {data && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {new Date(data.date).toLocaleDateString("de-DE",{day:"2-digit",month:"long",year:"numeric"})}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!data && <div className="py-6 text-sm text-muted-foreground">Lade…</div>}

        {data && data.rows.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {isDep ? "Heute keine Abreisen." : "Heute keine Anreisen."}
          </div>
        )}

        {data && data.rows.length > 0 && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {data.rows.map(row => (
              <div key={row.id}
                   className="rounded-2xl border border-border bg-white p-4 flex flex-wrap items-center gap-4 justify-between"
                   data-testid={`today-row-${row.id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-baseline gap-2">
                    <span className="inline-flex items-center gap-1 rounded-lg bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
                      {row.room_name}
                    </span>
                    <span className="font-semibold text-base">{row.guest_name}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} weight="bold"/> {row.persons} {row.persons === 1 ? "Person" : "Personen"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Moon size={12} weight="bold"/> {row.nights} {row.nights === 1 ? "Nacht" : "Nächte"}
                    </span>
                    {row.guest_phone && (
                      <span className="inline-flex items-center gap-1"><Phone size={12} weight="bold"/> {row.guest_phone}</span>
                    )}
                    {row.guest_email && (
                      <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} weight="bold"/> {row.guest_email}</span>
                    )}
                  </div>
                </div>
                <Button onClick={()=>trigger(row)}
                        className="rounded-xl bg-primary hover:bg-primary/90 text-white"
                        data-testid={`auto-send-${row.id}`}
                        disabled={!row.guest_phone && !row.guest_email}
                        title={channelsLabel(row)}>
                  <PaperPlaneTilt size={16} weight="fill"/>
                  <span className="ml-1.5">{btnLabel}</span>
                  <span className="ml-2 text-[10px] font-semibold opacity-80">· {channelsLabel(row)}</span>
                </Button>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground mt-2">
          Auto-Versand: bei Handynummer wird WhatsApp geöffnet, sonst E-Mail. Sind beide hinterlegt, wird beides gleichzeitig geöffnet.
          Absender: MainSpessartBlick WhatsApp Business 017625799945.
        </p>
      </DialogContent>
    </Dialog>
  );
}
